<template>
  <v-dialog v-model="model" max-width="480">
    <v-card rounded="lg">
      <v-card-title class="text-h6 pa-6 pb-4">
        <v-icon start color="primary">mdi-closed-caption-outline</v-icon>
        Choose caption language
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <v-list nav>
          <v-list-item
            v-for="lang in languages"
            :key="lang.code"
            :value="lang.code"
            :active="selectedLanguage === lang.code"
            color="primary"
            rounded="lg"
            class="mb-1"
            @click="handleSelect(lang.code)"
          >
            <template #prepend>
              <v-icon>{{ lang.icon }}</v-icon>
            </template>
            <v-list-item-title class="text-body-1 font-weight-medium">
              {{ lang.label }}
            </v-list-item-title>
            <v-list-item-subtitle>{{ lang.nativeLabel }}</v-list-item-subtitle>
            <template #append>
              <v-icon v-if="selectedLanguage === lang.code" color="primary">
                mdi-check
              </v-icon>
            </template>
          </v-list-item>
        </v-list>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="text" @click="model = false">Cancel</v-btn>
        <VBtnPrimary @click="handleConfirm">Apply</VBtnPrimary>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

interface Language {
  code: string
  label: string
  nativeLabel: string
  icon: string
}

interface Props {
  modelValue: boolean
  currentLanguage?: string
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'language-selected', code: string): void
}

const props = withDefaults(defineProps<Props>(), {
  currentLanguage: 'en',
})

const emit = defineEmits<Emits>()

const model = ref(props.modelValue)
const selectedLanguage = ref(props.currentLanguage)

watch(() => props.modelValue, (v) => { model.value = v })
watch(model, (v) => { emit('update:modelValue', v) })
watch(() => props.currentLanguage, (v) => { selectedLanguage.value = v })

const languages: Language[] = [
  { code: 'en', label: 'English', nativeLabel: 'English', icon: 'mdi-translate' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español', icon: 'mdi-translate' },
  { code: 'de', label: 'German', nativeLabel: 'Deutsch', icon: 'mdi-translate' },
  { code: 'fr', label: 'French', nativeLabel: 'Français', icon: 'mdi-translate' },
  { code: 'pt', label: 'Portuguese', nativeLabel: 'Português', icon: 'mdi-translate' },
  { code: 'ru', label: 'Russian', nativeLabel: 'Русский', icon: 'mdi-translate' },
  { code: 'ja', label: 'Japanese', nativeLabel: '日本語', icon: 'mdi-translate' },
  { code: 'zh', label: 'Chinese (Simplified)', nativeLabel: '中文（简体）', icon: 'mdi-translate' },
  { code: 'ko', label: 'Korean', nativeLabel: '한국어', icon: 'mdi-translate' },
  { code: 'it', label: 'Italian', nativeLabel: 'Italiano', icon: 'mdi-translate' },
  { code: 'nl', label: 'Dutch', nativeLabel: 'Nederlands', icon: 'mdi-translate' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', icon: 'mdi-translate' },
  { code: 'off', label: 'Turn off captions', nativeLabel: '', icon: 'mdi-closed-caption-off' },
]

function handleSelect(code: string) {
  selectedLanguage.value = code
}

function handleConfirm() {
  emit('language-selected', selectedLanguage.value)
  model.value = false
}
</script>
